package co.uk.paxes.pinpoint.model

/**
 * Analyzes board state for strategic AI decisions
 */
class BoardAnalyzer {
    private val scoreCalculator = ScoreCalculator()
    
    /**
     * Find positions where opponents could score high
     * Returns opportunities sorted by potential score (highest first)
     */
    fun findBlockingOpportunities(gameState: GameState): List<BlockingOpportunity> {
        val opportunities = mutableListOf<BlockingOpportunity>()
        val emptyPositions = getEmptyPositions(gameState.board)
        
        // Limit positions checked for performance (sample if too many)
        val positionsToCheck = if (emptyPositions.size > 20) {
            emptyPositions.shuffled().take(20)
        } else {
            emptyPositions
        }
        
        // For each opponent
        for (opponent in gameState.players.filter { it.id != gameState.currentPlayer.id }) {
            // Check each empty position with each color
            for (position in positionsToCheck) {
                for (color in PinColor.values()) {
                    if (color == PinColor.BLACK) continue // Skip black
                    
                    try {
                        val potentialScore = simulateScore(gameState.board, position, color)
                        if (potentialScore >= 4) { // Only track significant threats
                            opportunities.add(
                                BlockingOpportunity(position, opponent.id, potentialScore)
                            )
                        }
                    } catch (e: Exception) {
                        // Skip this position if simulation fails
                        continue
                    }
                }
            }
        }
        
        return opportunities.sortedByDescending { it.potentialScore }
    }
    
    /**
     * Find positions where AI could score with available pins
     * Returns opportunities sorted by potential score (highest first)
     */
    fun findScoringOpportunities(
        gameState: GameState,
        availablePins: List<Pin>
    ): List<ScoringOpportunity> {
        val opportunities = mutableListOf<ScoringOpportunity>()
        val emptyPositions = getEmptyPositions(gameState.board)
        
        for (pin in availablePins) {
            val color = pin.getEffectiveColor()
            if (color == PinColor.BLACK) continue // Skip black pins
            
            for (position in emptyPositions) {
                val score = simulateScore(gameState.board, position, color)
                if (score > 0) {
                    opportunities.add(
                        ScoringOpportunity(position, pin.color, score)
                    )
                }
            }
        }
        
        return opportunities.sortedByDescending { it.potentialScore }
    }
    
    /**
     * Analyze color density on the board
     * Returns map of color to count of pins of that color
     */
    fun analyzeColorDensity(board: Board): Map<PinColor, Int> {
        val density = mutableMapOf<PinColor, Int>()
        
        // Initialize all colors to 0
        for (color in PinColor.values()) {
            density[color] = 0
        }
        
        // Count pins of each color
        for (sectionRow in 0..2) {
            for (sectionCol in 0..2) {
                val maxLocalRow = if (sectionRow == 0) 3 else 2
                val maxLocalCol = if (sectionCol == 1) 3 else 2
                
                for (localRow in 0..maxLocalRow) {
                    for (localCol in 0..maxLocalCol) {
                        val pos = Position(sectionRow, sectionCol, localRow, localCol)
                        val pin = board.getPinAt(pos)
                        if (pin != null) {
                            val color = pin.getEffectiveColor()
                            density[color] = (density[color] ?: 0) + 1
                        }
                    }
                }
            }
        }
        
        return density
    }
    
    /**
     * Get all empty positions on the board
     */
    fun getEmptyPositions(board: Board): List<Position> {
        val empty = mutableListOf<Position>()
        
        for (sectionRow in 0..2) {
            for (sectionCol in 0..2) {
                val maxLocalRow = if (sectionRow == 0) 3 else 2
                val maxLocalCol = if (sectionCol == 1) 3 else 2
                
                for (localRow in 0..maxLocalRow) {
                    for (localCol in 0..maxLocalCol) {
                        val pos = Position(sectionRow, sectionCol, localRow, localCol)
                        if (board.isEmpty(pos)) {
                            empty.add(pos)
                        }
                    }
                }
            }
        }
        
        return empty
    }
    
    /**
     * Simulate the score that would be earned by placing a pin at a position
     */
    fun simulateScore(board: Board, position: Position, color: PinColor): Int {
        // Create a temporary pin for simulation
        val tempPin = Pin(color)
        val tempMove = Move("temp", tempPin, position)
        
        // Calculate score using the score calculator
        val scoreResult = scoreCalculator.calculateMoveScore(board, tempMove)
        return scoreResult.points
    }
    
    /**
     * Count pins of a specific color on the board
     */
    private fun countPinsOfColor(board: Board, color: PinColor): Int {
        var count = 0
        
        for (sectionRow in 0..2) {
            for (sectionCol in 0..2) {
                val maxLocalRow = if (sectionRow == 0) 3 else 2
                val maxLocalCol = if (sectionCol == 1) 3 else 2
                
                for (localRow in 0..maxLocalRow) {
                    for (localCol in 0..maxLocalCol) {
                        val pos = Position(sectionRow, sectionCol, localRow, localCol)
                        val pin = board.getPinAt(pos)
                        if (pin?.getEffectiveColor() == color) {
                            count++
                        }
                    }
                }
            }
        }
        
        return count
    }
}

/**
 * Represents an opportunity to block an opponent's high-scoring move
 */
data class BlockingOpportunity(
    val position: Position,
    val opponentId: String,
    val potentialScore: Int
)

/**
 * Represents an opportunity for the AI to score points
 */
data class ScoringOpportunity(
    val position: Position,
    val color: PinColor,
    val potentialScore: Int
)